import {useQuery} from 'react-query';
import {QueryKey} from 'react-query/types/core/types';
import {UseQueryOptions} from 'react-query/types/react/types';

import {
    SearchRequest, SearchResponse,
    ResourceResponse, SearchRequestParams
} from '../../api/types';
import {
    isNotFoundError,
    isResourceResponseError,
    resolveResourceResponseErrorMessage,
    resolveResponseData
} from '../../api/utils/resolve-response-data';
import {useSnackbar} from '../../components/snackbars/snackbars';
import {useAppContext} from '../app-context/use-app-context';

export const SCHOOL_ACADEMIC_YEARS_SEARCH_KEY = 'schoolAcademicYearsSearch';

export const postSchoolAcademicYearsSearchQueryKey = (
    options: SearchRequestOptions
): QueryKey => {
    return [SCHOOL_ACADEMIC_YEARS_SEARCH_KEY, options];
};

export type SearchRequestOptions = {
    request: SearchRequest,
    params: SearchRequestParams
}

export const useQuerySchoolAcademicYearsSearch = (
    searchOptions: SearchRequestOptions,
    queryOptions?: UseQueryOptions<SearchResponse>
) => {
    const {api: {schoolAcademicYearsSearch}} = useAppContext();
    const {enqueueSnackbar} = useSnackbar();

    return useQuery<SearchResponse>(
        postSchoolAcademicYearsSearchQueryKey(searchOptions),
        async () => {
            const response = await schoolAcademicYearsSearch(
                searchOptions.request,
                searchOptions.params
            );
            return resolveResponseData(response);
        }, {
            keepPreviousData: true,
            onError: (error: any | ResourceResponse) => {
                const message = resolveResourceResponseErrorMessage(error);
                enqueueSnackbar(message, {variant: isNotFoundError(error) ? 'info' : 'error'});
            },
            // Retry is necessary to account for errors that are not required to retry on
            // eg 404 on search results not found
            retry: (failureCount: number, error: any) => {
                const hasReachedMaxRetries = failureCount < 3;

                // Don't use the default retry on 404 errors,
                // the 404 is an expected response for an empty search
                if (isResourceResponseError(error)) {
                    return !hasReachedMaxRetries && !isNotFoundError(error);
                }

                if (hasReachedMaxRetries) return false;

                return true;
            },
            ...queryOptions
        });
};
